import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Navbar } from '../../components/feature/Navbar';
import { Footer } from '../../components/feature/Footer';
import { WhatsAppButton } from '../../components/feature/WhatsAppButton';
import { Button } from '../../components/base/Button';
import { ListingCard } from '../../components/base/ListingCard';
import { mockListings } from '../../mocks/listings';
import { mockFacilities } from '../../mocks/facilities';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedWeekCount, setSelectedWeekCount] = useState<string>('');
  const [currentHeroImage, setCurrentHeroImage] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    message: '',
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const heroImages = [
    'https://public.readdy.ai/ai/img_res/f1cc9b50791b017828f7ed92b6e54bc7.jpg',
    'https://readdy.ai/api/search-image?query=stunning%20beachfront%20resort%20sunset%20view%20golden%20hour%20orange%20sky%20luxury%20villas%20private%20beach%20white%20sand%20crystal%20clear%20water%20tropical%20paradise%20romantic%20atmosphere%20premium%20vacation%20destination&width=1920&height=1080&seq=hero-slide-2&orientation=landscape',
    'https://readdy.ai/api/search-image?query=elegant%20spa%20resort%20mountain%20view%20infinity%20pool%20overlooking%20valley%20lush%20green%20landscape%20modern%20minimalist%20architecture%20wellness%20retreat%20peaceful%20serene%20atmosphere%20luxury%20accommodation&width=1920&height=1080&seq=hero-slide-3&orientation=landscape',
    'https://readdy.ai/api/search-image?query=tropical%20resort%20garden%20view%20exotic%20flowers%20palm%20trees%20wooden%20walkways%20traditional%20architecture%20bungalows%20peaceful%20lagoon%20turquoise%20water%20paradise%20island%20vacation%20luxury%20escape&width=1920&height=1080&seq=hero-slide-4&orientation=landscape',
  ];

  const backgroundImages = heroImages;

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeroImage((prev) => (prev + 1) % heroImages.length);
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 8000);
    return () => clearInterval(interval);
  }, []);
  
  // Farklı tesislerden karışık ilanlar seç
  const featuredListings = [
    mockListings[0],   // Emin Termal
    mockListings[10],  // Limak Limra
    mockListings[11],  // Rixos Premium Belek
    mockListings[3],   // Emin Termal Deluxe
    mockListings[12],  // Hillside Beach Club
    mockListings[13],  // Maxx Royal Belek
    mockListings[14],  // Voyage Belek
    mockListings[22],  // Emin Termal (yeni)
  ];
  const popularFacilities = mockFacilities.slice(0, 6);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery) params.append('search', searchQuery);
    if (selectedWeekCount) params.append('weeks', selectedWeekCount);
    navigate(`/ilanlar?${params.toString()}`);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('loading');

    try {
      const response = await fetch('https://readdy.ai/api/form/d690g7tbd17lru29l2mg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          message: formData.message,
        }).toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        setFormData({ firstName: '', lastName: '', phone: '', message: '' });
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  const processSteps = [
    {
      icon: 'ri-search-line',
      title: 'İlanları İncele',
      description: 'Tesis, lokasyon ve hafta sayısına göre ilanları filtrele ve karşılaştır.',
    },
    {
      icon: 'ri-phone-line',
      title: 'İletişime Geç',
      description: 'Beğendiğin ilanlar için WhatsApp veya telefon ile hemen iletişime geç.',
    },
    {
      icon: 'ri-file-text-line',
      title: 'Güvenli İşlem',
      description: 'Doğrulanmış ilanlar ve şeffaf bilgilerle güvenle alım-satım yap.',
    },
    {
      icon: 'ri-star-line',
      title: 'Tatile Başla',
      description: 'Devremülk hakkını al ve hayalindeki tatil köyünde keyifli zamanlar geçir.',
    },
  ];

  const trustBlocks = [
    {
      icon: 'ri-shield-check-line',
      title: 'Doğrulanmış İlanlar',
      description: 'Tüm ilanlar detaylı kontrol edilir',
    },
    {
      icon: 'ri-message-3-line',
      title: 'Hızlı İletişim',
      description: 'WhatsApp ile anında bağlan',
    },
    {
      icon: 'ri-eye-line',
      title: 'Şeffaf Bilgi',
      description: 'Tüm detaylar açık ve net',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Images with Transition */}
        <div className="absolute inset-0">
          {backgroundImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ${
                currentImageIndex === index ? 'opacity-100' : 'opacity-0'
              }`}
              style={{ backgroundImage: `url(${image})` }}
            />
          ))}
        </div>
        
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>

        {/* Content */}
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 text-center pt-56">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Devremülk İlanları
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-12 max-w-3xl mx-auto leading-relaxed">
            Güvenle Al / Sat • Tesis, Hafta Sayısı ve Görselleriyle İlanları Karşılaştırın
          </p>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="max-w-4xl mx-auto mb-8">
            <div className="bg-white rounded-2xl shadow-2xl p-4 md:p-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Tesis veya lokasyon ara..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-6 py-4 text-base text-[#0A1628] bg-[#F7F9FC] rounded-xl border-2 border-transparent focus:border-[#D4AF37] focus:outline-none transition-colors"
                  />
                </div>
                <div className="w-full md:w-48">
                  <select
                    value={selectedWeekCount}
                    onChange={(e) => setSelectedWeekCount(e.target.value)}
                    className="w-full px-6 py-4 text-base text-[#0A1628] bg-[#F7F9FC] rounded-xl border-2 border-transparent focus:border-[#D4AF37] focus:outline-none transition-colors cursor-pointer"
                  >
                    <option value="">Hafta Sayısı</option>
                    <option value="1">1 Hafta</option>
                    <option value="2">2 Hafta</option>
                    <option value="3">3 Hafta</option>
                    <option value="4">4+ Hafta</option>
                  </select>
                </div>
                <Button type="submit" variant="primary" size="lg" className="whitespace-nowrap">
                  <i className="ri-search-line mr-2"></i>
                  Ara
                </Button>
              </div>
            </div>
          </form>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <Link to="/ilanlar">
              <Button variant="primary" size="lg">
                İlanları Gör
              </Button>
            </Link>
            <Link to="/ilan-ver">
              <Button variant="outline" size="lg">
                İlan Ver (Satışa Koy)
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Blocks */}
      <section className="py-16 bg-white border-b border-[#E5E9F0]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {trustBlocks.map((block, index) => (
              <div key={index} className="flex items-start gap-4">
                <div className="w-14 h-14 flex items-center justify-center bg-[#D4AF37]/10 rounded-xl flex-shrink-0">
                  <i className={`${block.icon} text-2xl text-[#D4AF37]`}></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#0A1628] mb-2">
                    {block.title}
                  </h3>
                  <p className="text-base text-[#6B7A90]">
                    {block.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Listings Section */}
      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0A1628]">
              Öne Çıkan İlanlar
            </h2>
            <Link
              to="/ilanlar"
              className="text-[#D4AF37] font-medium hover:underline cursor-pointer flex items-center whitespace-nowrap"
            >
              Tümünü Gör
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {featuredListings.map((listing) => (
              <ListingCard key={listing.id} {...listing} />
            ))}
          </div>
        </div>
      </section>

      {/* Popular Facilities Section */}
      <section className="py-24 bg-[#F7F9FC]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0A1628]">
              Popüler Tesisler
            </h2>
            <Link
              to="/tesisler"
              className="text-[#D4AF37] font-medium hover:underline cursor-pointer flex items-center whitespace-nowrap"
            >
              Tümünü Gör
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {popularFacilities.map((facility) => (
              <Link
                key={facility.id}
                to={`/tesis/${facility.slug}`}
                className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={facility.image}
                    alt={facility.name}
                    className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full">
                    <span className="text-sm font-semibold text-[#0A1628]">
                      {facility.listing_count} İlan
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2 group-hover:text-[#D4AF37] transition-colors">
                    {facility.name}
                  </h3>
                  <p className="text-sm text-[#6B7A90] mb-4 flex items-center">
                    <i className="ri-map-pin-line mr-2"></i>
                    {facility.location}
                  </p>
                  <p className="text-sm text-[#6B7A90] line-clamp-2 mb-4">
                    {facility.description}
                  </p>
                  <div className="flex items-center text-[#D4AF37] font-medium">
                    İlanları Gör
                    <i className="ri-arrow-right-line ml-2 group-hover:translate-x-2 transition-transform"></i>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <h2 className="text-4xl md:text-5xl font-bold text-[#0A1628] text-center mb-16">
            Nasıl Çalışır?
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {processSteps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-[#D4AF37]/10 rounded-2xl mx-auto mb-6">
                  <i className={`${step.icon} text-4xl text-[#D4AF37]`}></i>
                </div>
                <h3 className="text-xl font-semibold text-[#0A1628] mb-3">
                  {step.title}
                </h3>
                <p className="text-base text-[#6B7A90] leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-24 bg-[#F7F9FC]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#0A1628] mb-6">
                Bizi Arayın
              </h2>
              <p className="text-lg text-[#6B7A90] mb-8 leading-relaxed">
                Devremülk alım-satım sürecinizde size yardımcı olmaktan mutluluk duyarız. Formu doldurun, en kısa sürede sizinle iletişime geçelim.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 flex items-center justify-center bg-[#D4AF37]/10 rounded-xl">
                    <i className="ri-phone-line text-2xl text-[#D4AF37]"></i>
                  </div>
                  <div>
                    <p className="text-sm text-[#6B7A90]">Telefon</p>
                    <a href="tel:+905443953935" className="text-lg font-semibold text-[#0A1628] hover:text-[#D4AF37] transition-colors cursor-pointer">
                      +90 544 395 39 35
                    </a>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 flex items-center justify-center bg-[#D4AF37]/10 rounded-xl">
                    <i className="ri-whatsapp-line text-2xl text-[#D4AF37]"></i>
                  </div>
                  <div>
                    <p className="text-sm text-[#6B7A90]">WhatsApp</p>
                    <a href="https://wa.me/905443953935" target="_blank" rel="noopener noreferrer" className="text-lg font-semibold text-[#0A1628] hover:text-[#D4AF37] transition-colors cursor-pointer">
                      Hemen Yazın
                    </a>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 flex items-center justify-center bg-[#D4AF37]/10 rounded-xl">
                    <i className="ri-mail-line text-2xl text-[#D4AF37]"></i>
                  </div>
                  <div>
                    <p className="text-sm text-[#6B7A90]">E-posta</p>
                    <a href="mailto:info.sapphire.gayrimenkul@gmail.com" className="text-lg font-semibold text-[#0A1628] hover:text-[#D4AF37] transition-colors cursor-pointer">
                      info.sapphire.gayrimenkul@gmail.com
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Form */}
            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10">
              <h3 className="text-2xl font-bold text-[#0A1628] mb-2">Bize Ulaşın</h3>
              <p className="text-[#6B7A90] mb-8">Bilgilerinizi bırakın, sizi arayalım</p>
              
              {formStatus === 'success' ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-6">
                    <i className="ri-check-line text-4xl text-green-600"></i>
                  </div>
                  <h4 className="text-xl font-semibold text-[#0A1628] mb-2">Mesajınız Alındı!</h4>
                  <p className="text-[#6B7A90]">En kısa sürede sizinle iletişime geçeceğiz.</p>
                </div>
              ) : (
                <form 
                  id="contact-form"
                  data-readdy-form
                  onSubmit={handleFormSubmit}
                  className="space-y-6"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-[#0A1628] mb-2">
                        İsim *
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleFormChange}
                        required
                        placeholder="İsminiz"
                        className="w-full px-4 py-3 text-sm text-[#0A1628] bg-[#F7F9FC] rounded-xl border-2 border-transparent focus:border-[#D4AF37] focus:outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-[#0A1628] mb-2">
                        Soyisim *
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleFormChange}
                        required
                        placeholder="Soyisminiz"
                        className="w-full px-4 py-3 text-sm text-[#0A1628] bg-[#F7F9FC] rounded-xl border-2 border-transparent focus:border-[#D4AF37] focus:outline-none transition-colors"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-[#0A1628] mb-2">
                      Telefon Numarası *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleFormChange}
                      required
                      placeholder="05XX XXX XX XX"
                      className="w-full px-4 py-3 text-sm text-[#0A1628] bg-[#F7F9FC] rounded-xl border-2 border-transparent focus:border-[#D4AF37] focus:outline-none transition-colors"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-[#0A1628] mb-2">
                      Mesajınız
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleFormChange}
                      rows={4}
                      maxLength={500}
                      placeholder="Mesajınızı buraya yazın..."
                      className="w-full px-4 py-3 text-sm text-[#0A1628] bg-[#F7F9FC] rounded-xl border-2 border-transparent focus:border-[#D4AF37] focus:outline-none transition-colors resize-none"
                    ></textarea>
                    <p className="text-xs text-[#6B7A90] mt-1 text-right">{formData.message.length}/500</p>
                  </div>

                  {formStatus === 'error' && (
                    <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl text-sm">
                      Bir hata oluştu. Lütfen tekrar deneyin.
                    </div>
                  )}
                  
                  <Button 
                    type="submit" 
                    variant="primary" 
                    size="lg" 
                    className="w-full whitespace-nowrap"
                    disabled={formStatus === 'loading'}
                  >
                    {formStatus === 'loading' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Gönderiliyor...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line mr-2"></i>
                        Gönder
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0A1628]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Devremülk Satmak mı İstiyorsunuz?
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            İlanınızı hemen yayınlayın ve binlerce potansiyel alıcıya ulaşın
          </p>
          <Link to="/ilan-ver">
            <Button variant="primary" size="lg">
              Ücretsiz İlan Ver
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default HomePage;